import { ChevronRight, type LucideIcon, Star } from "lucide-react";
import Link from "next/link";

import { dormantSeasonTestIds } from "@/lib/data-test-ids/components.data-test-ids";
import { cn } from "@/lib/utils";

export type DormantSeasonAction = {
  label: string;
  description?: string;
  href: string;
  icon: LucideIcon;
};

type DormantSeasonPanelViewProps = {
  title: string;
  description: string;
  actions: DormantSeasonAction[];
  className?: string;
};

export default function DormantSeasonPanelView({
  title,
  description,
  actions,
  className,
}: DormantSeasonPanelViewProps) {
  return (
    <div
      className={cn(
        "flex flex-auto justify-center overflow-auto px-4",
        className,
      )}
      data-testid={dormantSeasonTestIds.PANEL}
    >
      <div className="flex w-full max-w-xl flex-col items-center pt-12 pb-8">
        <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
          <Star className="h-8 w-8 text-primary" />
        </div>
        <h3
          className="mb-3 text-center text-xl font-bold text-foreground sm:text-2xl"
          data-testid={dormantSeasonTestIds.PANEL_TITLE}
        >
          {title}
        </h3>
        <p
          className="mb-8 text-center text-sm leading-relaxed text-muted-foreground"
          data-testid={dormantSeasonTestIds.PANEL_DESCRIPTION}
        >
          {description}
        </p>

        {actions.length > 0 && (
          <>
            <p className="mb-3 w-full text-xs font-semibold tracking-wide text-ink-subtle uppercase">
              Keep your search going
            </p>
            <ul className="flex w-full flex-col gap-2">
              {actions.map((action) => (
                <li key={action.label}>
                  <Link
                    href={action.href}
                    className="flex items-center gap-3 rounded-lg border border-border bg-card px-4 py-3 transition-colors hover:bg-surface-subtle"
                  >
                    <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-primary/10">
                      <action.icon className="h-4 w-4 text-primary" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium text-foreground">
                        {action.label}
                      </span>
                      {action.description && (
                        <span className="block text-xs text-ink-subtle">
                          {action.description}
                        </span>
                      )}
                    </span>
                    <ChevronRight className="h-4 w-4 flex-shrink-0 text-ink-subtle" />
                  </Link>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </div>
  );
}
